import React from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarDays, ChevronDown, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { submitLead } from "@/lib/api";

const SERVICES = [
  "House Washing",
  "Driveways & Sidewalks",
  "Roof Cleaning",
  "Patios & Pool Decks",
  "Fence Cleaning",
  "Window Cleaning",
  "Commercial / Multi-property",
  "Other / Not sure",
];

const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

export default function EstimateForm() {
  const [form, setForm] = React.useState({
    name: "", phone: "", email: "", address: "",
    service_type: "House Washing", message: "",
  });
  const [date, setDate] = React.useState(null);
  const [time, setTime] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [bookOnline, setBookOnline] = React.useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    if (bookOnline && (!date || !time)) {
      toast.error("Pick a preferred date and time, or switch to 'Free Estimate'.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        kind: bookOnline ? "booking" : "estimate",
        preferred_date: bookOnline && date ? format(date, "yyyy-MM-dd") : null,
        preferred_time: bookOnline ? time : null,
      };
      await submitLead(payload);
      toast.success(
        bookOnline
          ? "Booking request received! We'll call to confirm shortly."
          : "Thanks! We'll be in touch with your free estimate."
      );
      setForm({ name: "", phone: "", email: "", address: "", service_type: "House Washing", message: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error("Couldn't send your request. Please call 407-375-2862.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="relative py-20 md:py-28 bg-[var(--fc-surface)]" data-testid="estimate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
        {/* Left: contact info */}
        <div className="lg:col-span-2" id="contact" data-testid="contact-block">
          <div className="font-script text-3xl text-[var(--fc-orange)]">Get in touch</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[var(--fc-blue-dark)] mt-1">
            Request your free estimate.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Tell us about your property and we'll get back fast — usually same day. Or call/text us anytime.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href="tel:4073752862"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-blue-100 hover:-translate-y-0.5 transition-transform"
              data-testid="contact-phone"
            >
              <div className="w-12 h-12 btn-orange rounded-2xl grid place-items-center"><Phone className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Call or Text</div>
                <div className="font-heading text-xl font-bold text-[var(--fc-blue-dark)]">407-375-2862</div>
              </div>
            </a>
            <a
              href="mailto:Faithfullycleansolutionsllc@gmail.com"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-blue-100 hover:-translate-y-0.5 transition-transform"
              data-testid="contact-email"
            >
              <div className="w-12 h-12 btn-blue rounded-2xl grid place-items-center"><Mail className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-heading text-lg font-bold text-[var(--fc-blue-dark)] break-all">
                  Faithfullycleansolutionsllc@gmail.com
                </div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-blue-100">
              <div className="w-12 h-12 rounded-2xl bg-[var(--fc-blue-dark)] grid place-items-center"><MapPin className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Service Area</div>
                <div className="font-heading text-lg font-bold text-[var(--fc-blue-dark)] leading-tight">
                  Based in St. Cloud<br />
                  <span className="text-sm font-bold text-slate-600">Serving all of Central Florida</span>
                </div>
              </div>
            </div>

            <a
              href="https://www.google.com/search?sa=X&sca_esv=0444657f1031709e&hl=en&authuser=0&biw=390&bih=699&sxsrf=ANbL-n5AF-t3QSGrTSethgx8G9d2e7KcEQ%3A1781379695575&kgmid=%2Fg%2F11zfq70sdd&q=Faithfully%20Clean%20Pressure%20Washing&shem=epsd1%2Crimspwouoe&shndl=30&source=sh%2Fx%2Floc%2Fact%2Fm4%2F3"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-blue-100 hover:-translate-y-0.5 transition-transform"
              data-testid="contact-google"
            >
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 grid place-items-center">
                <svg viewBox="0 0 48 48" className="w-6 h-6" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 43.5c5.3 0 10.1-2 13.7-5.3l-6.3-5.3c-2 1.4-4.6 2.3-7.4 2.3-5.3 0-9.7-3.4-11.3-8.1L6 31.4C9.3 38.6 16.1 43.5 24 43.5z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.7 35.4 43.5 30 43.5 24c0-1.2-.1-2.3 .1-3.5z"/>
                </svg>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Reviews</div>
                <div className="font-heading text-lg font-bold text-[var(--fc-blue-dark)]">Find us on Google</div>
              </div>
            </a>
          </div>
        </div>

        {/* Right: form */}
        <form
          onSubmit={submit}
          className="lg:col-span-3 bg-white rounded-3xl p-7 sm:p-10 border border-blue-100 shadow-2xl shadow-blue-900/10"
          data-testid="estimate-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => setBookOnline(false)}
              className={`px-4 h-11 rounded-full font-heading font-bold uppercase tracking-wider text-sm transition-all ${!bookOnline ? "btn-orange" : "bg-slate-100 text-slate-600"}`}
              data-testid="tab-estimate"
            >
              Free Estimate
            </button>
            <button
              type="button"
              onClick={() => setBookOnline(true)}
              className={`px-4 h-11 rounded-full font-heading font-bold uppercase tracking-wider text-sm transition-all ${bookOnline ? "btn-orange" : "bg-slate-100 text-slate-600"}`}
              data-testid="tab-booking"
            >
              Book a Date
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="font-bold text-slate-700">Name *</Label>
              <Input id="name" data-testid="form-name" value={form.name} onChange={update("name")}
                     className="h-12 rounded-xl mt-1" placeholder="Your full name" required />
            </div>
            <div>
              <Label htmlFor="phone" className="font-bold text-slate-700">Phone *</Label>
              <Input id="phone" data-testid="form-phone" value={form.phone} onChange={update("phone")}
                     className="h-12 rounded-xl mt-1" placeholder="(407) 555-0123" required />
            </div>
            <div>
              <Label htmlFor="email" className="font-bold text-slate-700">Email</Label>
              <Input id="email" type="email" data-testid="form-email" value={form.email} onChange={update("email")}
                     className="h-12 rounded-xl mt-1" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="address" className="font-bold text-slate-700">Property Address</Label>
              <Input id="address" data-testid="form-address" value={form.address} onChange={update("address")}
                     className="h-12 rounded-xl mt-1" placeholder="City, FL" />
            </div>

            <div className="sm:col-span-2">
              <Label className="font-bold text-slate-700">Service Needed</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-left flex items-center justify-between hover:border-[var(--fc-blue)]"
                    data-testid="form-service-trigger"
                  >
                    <span className="text-slate-800">{form.service_type}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
                  {SERVICES.map((s) => (
                    <DropdownMenuItem
                      key={s}
                      onSelect={() => setForm((f) => ({ ...f, service_type: s }))}
                      data-testid={`form-service-option-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    >
                      {s}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {bookOnline && (
              <>
                <div>
                  <Label className="font-bold text-slate-700">Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="mt-1 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-left flex items-center justify-between hover:border-[var(--fc-blue)]"
                        data-testid="form-date-trigger"
                      >
                        <span className={date ? "text-slate-800" : "text-slate-400"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarDays className="w-4 h-4 text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="font-bold text-slate-700">Preferred Time</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="mt-1 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-left flex items-center justify-between hover:border-[var(--fc-blue)]"
                        data-testid="form-time-trigger"
                      >
                        <span className={time ? "text-slate-800" : "text-slate-400"}>
                          {time || "Pick a time"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-auto">
                      {TIME_SLOTS.map((t) => (
                        <DropdownMenuItem key={t} onSelect={() => setTime(t)} data-testid={`form-time-${t.replace(/\s|:/g, '')}`}>
                          {t}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            )}

            <div className="sm:col-span-2">
              <Label htmlFor="message" className="font-bold text-slate-700">
                Tell us about the job
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={update("message")}
                placeholder="Anything we should know? Square footage, problem areas, mold, gates, pets, etc."
                className="mt-1 rounded-xl min-h-[110px]"
                data-testid="form-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 btn-orange font-heading font-extrabold uppercase tracking-wider h-14 rounded-full text-base disabled:opacity-70"
            data-testid="form-submit"
          >
            {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
            {bookOnline ? "Request Booking" : "Get My Free Estimate"}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">
            No spam. We only use your info to follow up on this request.
          </p>
        </form>
      </div>
    </section>
  );
}
